import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';

interface AdditionalAction {
  label: string;
  route: string;
  queryParams?: Record<string, any>;
}

export type EntityType = 'user' | 'organization' | 'drone' | 'manufacturer' | 'model';
export type EntityAction = 'created' | 'existing';

export interface SuccessEntity {
  type: EntityType;
  /** Whether this entity was newly created or already existed (e.g. linked, reused). */
  action: EntityAction;
  name: string;
  /** Optional override for "View All …" navigation. Falls back to the static map by type. */
  listRoute?: string;
}

interface EntitySuccessState {
  entities: SuccessEntity[];
  message?: string;
  details?: Record<string, string>;
  createAnotherRoute?: string;
  additionalActions?: AdditionalAction[];
}

/**
 * Entity Success Component
 *
 * A reusable success page displayed after creating (or linking) one or more entities.
 *
 * Usage:
 *   this.router.navigate(['/success'], {
 *     state: {
 *       entities: [
 *         { type: 'user',         action: 'created',  name: 'John Doe' },
 *         { type: 'organization', action: 'existing', name: 'Acme Corp' },
 *       ],
 *       details: { ... },
 *       additionalActions: [ ... ],
 *     }
 *   });
 */
@Component({
  selector: 'app-entity-success',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './entity-success.component.html',
  styleUrls: ['./entity-success.component.scss']
})
export class EntitySuccessComponent implements OnInit {
  entities: SuccessEntity[] = [];
  message = '';
  details: Record<string, string> = {};
  createAnotherRoute: string | null = null;
  additionalActions: AdditionalAction[] = [];

  // Confetti pieces for animation - tripled for more celebration!
  confettiPieces = Array(150).fill(0);

  // Firework bursts
  fireworks = Array(6).fill(0);

  private entityTypeLabels: Record<EntityType, string> = {
    user: 'User',
    organization: 'Organization',
    drone: 'Drone',
    manufacturer: 'Manufacturer',
    model: 'Model',
  };

  private entityListRoutes: Record<EntityType, string> = {
    user: '/users',
    organization: '/organizations',
    drone: '/drones',
    manufacturer: '/drone-catalog',
    model: '/drone-catalog',
  };

  private entityTypeIcons: Record<EntityType, string> = {
    user: 'bi bi-person-check-fill',
    organization: 'bi bi-building-check',
    drone: 'adn-icon adn-icon-drone-solid',
    manufacturer: 'bi bi-cpu-fill',
    model: 'bi bi-airplane',
  };

  constructor(private router: Router) {}

  ngOnInit(): void {
    const state = history.state as EntitySuccessState;

    if (!state || !state.entities || state.entities.length === 0) {
      this.router.navigate(['/dashboard']);
      return;
    }

    this.entities = state.entities;
    this.details = state.details || {};
    this.createAnotherRoute = state.createAnotherRoute || null;
    this.additionalActions = state.additionalActions || [];
    this.message = state.message || this.buildDefaultMessage();
  }

  /** Page title — composed from entity actions. e.g. "User, Organization Created!" */
  get title(): string {
    const created = this.entities.filter(e => e.action === 'created');
    const existing = this.entities.filter(e => e.action === 'existing');

    const parts: string[] = [];
    if (created.length) {
      parts.push(`${created.map(e => this.getEntityTypeLabel(e.type)).join(', ')} Created`);
    }
    if (existing.length) {
      parts.push(`${existing.map(e => this.getEntityTypeLabel(e.type)).join(', ')} Linked`);
    }
    return parts.join(' · ') + '!';
  }

  private buildDefaultMessage(): string {
    const created = this.entities.filter(e => e.action === 'created').map(e => this.getEntityTypeLabel(e.type).toLowerCase());
    const existing = this.entities.filter(e => e.action === 'existing').map(e => this.getEntityTypeLabel(e.type).toLowerCase());

    if (created.length && !existing.length) {
      return `${this.capitalize(created.join(' and '))} created successfully.`;
    }
    if (!created.length && existing.length) {
      return `Existing ${existing.join(' and ')} linked successfully.`;
    }
    return `${this.capitalize(created.join(' and '))} created and linked to existing ${existing.join(' and ')}.`;
  }

  /** Unique entity types in `entities`, preserving order — used for "View All …" buttons. */
  get uniqueTypes(): EntityType[] {
    return Array.from(new Set(this.entities.map(e => e.type)));
  }

  get detailEntries(): [string, string][] {
    return Object.entries(this.details);
  }

  getEntityTypeLabel(type: EntityType): string {
    return this.entityTypeLabels[type] || 'Entity';
  }

  getEntityTypeLabelPlural(type: EntityType): string {
    const l = this.getEntityTypeLabel(type);
    return l === 'Entity' ? 'Entities' : `${l}s`;
  }

  getEntityIcon(type: EntityType): string {
    return this.entityTypeIcons[type] || 'bi bi-check-circle-fill';
  }

  navigateToList(type: EntityType): void {
    // Prefer a per-entity override (handles dynamic routes like
    // `/drone-catalog/${manufacturerId}`); fall back to the static map.
    const override = this.entities.find(e => e.type === type)?.listRoute;
    this.router.navigate([override || this.entityListRoutes[type] || '/dashboard']);
  }

  navigateToAction(route: string, queryParams?: Record<string, any>): void {
    this.router.navigate([route], queryParams ? { queryParams } : undefined);
  }

  createAnother(): void {
    if (this.createAnotherRoute) this.router.navigate([this.createAnotherRoute]);
  }

  private capitalize(s: string): string {
    return s.charAt(0).toUpperCase() + s.slice(1);
  }
}
